// PaperSignatureModal — option C of the approval flow ("Document upload"):
// single step: confirm per-item decisions + upload the signed paper photo
// (+ optional notify customer), all on one page.
// On submit, POST /quote/paper-authorization creates a quote_authorization
// row with the GCS blob as evidence and applies the decisions through the
// state machine. Mirrors the DigitalSignatureModal single-step flow.

import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  FileButton,
  Group,
  Loader,
  LoadingOverlay,
  Modal,
  ScrollArea,
  SegmentedControl,
  Slider,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconCrop, IconUpload, IconX } from '@tabler/icons-react';
import { quoteApi, storageApi } from '../api/client';
import { CaptureDocTypePicker } from '../../components/CaptureDocTypePicker';

type SignatureResult = {
  signature_detected: boolean;
  signature_box: [number, number, number, number] | null;
  page_w: number;
  page_h: number;
  confidence: number | null;
};

type Decision = 'approved' | 'declined';
type SendChannel = 'skip' | 'sms' | 'email';

const BG = '#0C0C0E';
const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_SECONDARY = '#A1A1AA';
const TEXT_MUTED = '#71717A';
const GREEN = '#22C55E';
const RED = '#EF4444';
const BLUE = '#2563EB';

type ItemRow = {
  source_id?: string | null;
  label?: string | null;
  amount?: number | string | null;
  default_decision?: 'approve' | 'decline' | 'skip';
};

type Props = {
  opened: boolean;
  onClose: () => void;
  conversationId: number;
  items: ItemRow[];
  customerHasContact?: boolean;
  customerName?: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  // Which legal-doc track this capture writes to: 'estimate' (default) or 'invoice'.
  docType?: 'estimate' | 'invoice' | 'both';
  // Choosing the track is the first step inside this pop-up; changing it asks the
  // parent to re-scope the eligible items for the new track.
  onDocTypeChange?: (v: 'estimate' | 'invoice' | 'both') => void;
  onApplied: (opts: { notify: boolean; channel?: SendChannel; phone?: string; email?: string }) => void;
};

export function PaperSignatureModal({
  opened,
  onClose,
  conversationId,
  items,
  customerHasContact = false,
  customerName = '',
  customerPhone = null,
  customerEmail = null,
  docType = 'estimate',
  onDocTypeChange,
  onApplied,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [authorizerName, setAuthorizerName] = useState(customerName || '');
  const [decisions, setDecisions] = useState<Record<string, Decision>>(
    Object.fromEntries(items.map((i) => {
      const intent: Decision = i.default_decision === 'decline' ? 'declined' : 'approved';
      return [String(i.source_id ?? ''), intent];
    })),
  );
  const [phone, setPhone] = useState(customerPhone || '');
  const [email, setEmail] = useState(customerEmail || '');
  const [sendChannel, setSendChannel] = useState<SendChannel>('skip');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadedBlob, setUploadedBlob] = useState<string | null>(null);
  const [signatureCheckRunning, setSignatureCheckRunning] = useState(false);
  const [signatureResult, setSignatureResult] = useState<SignatureResult | null>(null);
  // Binarize controls — view-only preview. At Save, if `binarize` is on the
  // canvas is rendered at full resolution, encoded as JPEG, and uploaded as
  // the stored evidence (replacing the raw photo). DocAI check stays on the
  // raw upload since that's the most permissive surface for detection.
  // Default ON: real shop photos are noisy + the AI signature detector
  // misses pen strokes on a low-contrast camera shot. Auto-crop + binarize
  // gives the model a clean B&W document image to look at and dramatically
  // improves recall on the signature-detected check.
  const [binarize, setBinarize] = useState(true);
  const [binarizeThreshold, setBinarizeThreshold] = useState(140);
  // User-adjustable crop box around the detected signature region. Seeded
  // from signatureResult.signature_box (no padding — tight to the cursive).
  // Stored in source-image pixel coords as [x1, y1, x2, y2]. Null until
  // DocAI returns a bbox.
  const [cropBox, setCropBox] = useState<[number, number, number, number] | null>(null);
  // Overview thumbnail with the draggable crop rectangle is hidden by
  // default to keep the modal focused on confirming the crop. Writer
  // opens it via the "Re-crop" button when they want to adjust.
  const [showRecrop, setShowRecrop] = useState(false);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (opened) {
      setFile(null);
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      setAuthorizerName(customerName || '');
      setPhone(customerPhone || '');
      setEmail(customerEmail || '');
      setSendChannel('skip');
      setError('');
      setDecisions(Object.fromEntries(items.map((i) => {
        const intent: Decision = i.default_decision === 'decline' ? 'declined' : 'approved';
        return [String(i.source_id ?? ''), intent];
      })));
      setUploadedBlob(null);
      setSignatureResult(null);
      setSignatureCheckRunning(false);
      setBinarize(true);
      setBinarizeThreshold(140);
      setCropBox(null);
      setShowRecrop(false);
    }
  }, [opened, customerName, customerPhone, customerEmail, items]);

  // Seed the crop box from DocAI's detected signature_box, no padding.
  // Matches the demo script's bbox (tight to the cursive). User can drag
  // the box afterward if they want more breathing room around it.
  useEffect(() => {
    const sb = signatureResult?.signature_box;
    if (!sb || !signatureResult?.page_w || !signatureResult?.page_h) {
      setCropBox(null);
      return;
    }
    setCropBox([sb[0], sb[1], sb[2], sb[3]]);
  }, [signatureResult]);

  const handleFileChange = (f: File | null) => {
    setFile(f);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const next = f ? URL.createObjectURL(f) : null;
    previewUrlRef.current = next;
    setPreviewUrl(next);
    setError('');
    setUploadedBlob(null);
    setSignatureResult(null);
    if (f) {
      void runSignatureCheck(f);
    }
  };

  const runSignatureCheck = async (f: File) => {
    setSignatureCheckRunning(true);
    setUploading(true);
    try {
      // Upload the RAW camera photo for DocAI. Empirically the cleanup chain
      // (autocrop + binarize) destroys the signature signal — DocAI returns
      // 0 entities on the binarized blob but 0.86 confidence on the original.
      // The cleanup is reserved for the evidence-upload path in handleConfirm.
      const { blob_name } = await storageApi.uploadToPresignedUrl(f);
      setUploadedBlob(blob_name);
      const result = await quoteApi.signatureCheck(conversationId, blob_name, f.type || 'image/jpeg');
      setSignatureResult(result);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Signature check failed.');
    } finally {
      setSignatureCheckRunning(false);
      setUploading(false);
    }
  };

  const setDecision = (sid: string, d: Decision) => {
    setDecisions((prev) => ({ ...prev, [sid]: d }));
  };

  const handleClose = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setFile(null);
    setPreviewUrl(null);
    setAuthorizerName(customerName || '');
    setPhone(customerPhone || '');
    setEmail(customerEmail || '');
    setSendChannel('skip');
    setError('');
    setSaving(false);
    setUploading(false);
    setUploadedBlob(null);
    setSignatureResult(null);
    setSignatureCheckRunning(false);
    onClose();
  };

  const handleSave = async () => {
    setError('');
    if (!file || !uploadedBlob) {
      setError('Upload a photo of the signed authorization first.');
      return;
    }
    if (!signatureResult?.signature_detected) {
      setError('No signature detected in this photo. Retake the photo or pick another.');
      return;
    }
    if (sendChannel === 'sms' && !phone.trim()) {
      setError('Enter a phone number or pick Skip.');
      return;
    }
    if (sendChannel === 'email' && !email.trim()) {
      setError('Enter an email address or pick Skip.');
      return;
    }
    setSaving(true);
    try {
      // Evidence upload uses the user-adjusted crop box (seeded from
      // DocAI's bbox, then movable in the preview) + optional binarize.
      // The raw photo is already in GCS as `uploadedBlob` for the AI
      // check; this just produces a cleaner zoomed evidence asset
      // ("just the signature, not the whole RO printout") for the
      // legal-doc record. Falls back to the raw upload on canvas error.
      let evidenceBlob = uploadedBlob;
      if (file && cropBox) {
        try {
          const cleaned = await renderCropAndBinarize(file, cropBox, binarize ? binarizeThreshold : null);
          const { blob_name } = await storageApi.uploadToPresignedUrl(cleaned);
          evidenceBlob = blob_name;
        } catch (cleanupErr) {
          console.warn('[paper-modal] save-time crop/binarize failed; using raw upload', cleanupErr);
        }
      }
      const payload: Record<string, 'approved' | 'declined'> = {};
      for (const [sid, d] of Object.entries(decisions)) payload[sid] = d as 'approved' | 'declined';
      // "Both" records the same signature against both tracks. The backend dup
      // guard is scoped by (quote, blob, doc_type), so one blob can legitimately
      // back one estimate AND one invoice doc; a same-track repeat 409s (handled
      // per-track below for retry safety).
      const tracks = docType === 'both' ? (['estimate', 'invoice'] as const) : [docType];
      for (const t of tracks) {
        try {
          const result = await quoteApi.paperAuthorization(
            conversationId,
            evidenceBlob,
            payload,
            authorizerName.trim() || undefined,
            t,
          );
          if (result.rejected?.length) {
            setError(`Some items were rejected: ${result.rejected.join('; ')}`);
            setSaving(false);
            return;
          }
        } catch (err) {
          // 409 = this track already has this signature on file (e.g. retrying
          // "Both" after a partial failure committed the estimate track). Treat
          // as already-applied and continue to the next track.
          if ((err as { response?: { status?: number } })?.response?.status === 409) continue;
          throw err;
        }
      }
      onApplied({
        notify: sendChannel !== 'skip',
        channel: sendChannel,
        phone: phone.trim(),
        email: email.trim(),
      });
      handleClose();
    } catch (e: unknown) {
      setUploading(false);
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to record authorization.');
      setSaving(false);
    }
  };

  const approvedCount = Object.values(decisions).filter((d) => d === 'approved').length;
  const declinedCount = Object.values(decisions).filter((d) => d === 'declined').length;

  return (
    <Modal
      opened={opened}
      onClose={saving ? () => {} : handleClose}
      withCloseButton={!saving}
      size="lg"
      title={<Text size="sm" fw={600} c={TEXT}>Document upload — paper signature</Text>}
      styles={{
        content: { background: CARD_BG, border: `1px solid ${BORDER}` },
        header: { background: CARD_BG },
        overlay: { backdropFilter: 'blur(4px)' },
      }}
      radius="md"
    >
      <Box pos="relative">
        <LoadingOverlay visible={saving} overlayProps={{ blur: 1, backgroundOpacity: 0.4 }} />
        <Stack gap={14}>
          {error && (
            <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" styles={{ message: { fontSize: 13 } }}>
              {error}
            </Alert>
          )}

              {onDocTypeChange && (
                <CaptureDocTypePicker value={docType} onChange={onDocTypeChange} hint="What the paper authorizes" />
              )}
              <Box>
                <Group justify="space-between" mb={6}>
                  <Text size="xs" c={TEXT_MUTED}>Confirm each line — paper will lock these in</Text>
                  <Text size="xs" c={TEXT_MUTED}>
                    <Text span c={GREEN} fw={600}>{approvedCount}</Text> approved ·
                    <Text span c={RED} fw={600} ml={4}>{declinedCount}</Text> declined
                  </Text>
                </Group>
                <ScrollArea.Autosize mah={360}>
                  <Stack gap={6}>
                    {items.map((it) => {
                      const sid = String(it.source_id ?? '');
                      const d = decisions[sid] ?? 'approved';
                      return (
                        <Box key={sid} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          gap: 10, padding: '10px 12px',
                          background: BG, border: `1px solid ${BORDER}`, borderRadius: 8,
                        }}>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Text size="sm" c={TEXT} fw={500} truncate>{it.label || '(no label)'}</Text>
                            <Text size="xs" c={TEXT_MUTED}>${Number(it.amount || 0).toFixed(2)}</Text>
                          </Box>
                          <Group gap={4} style={{ flexShrink: 0 }}>
                            <DecisionBtn label="Approve" icon={<IconCheck size={12} />} color={GREEN} active={d === 'approved'} onClick={() => setDecision(sid, 'approved')} />
                            <DecisionBtn label="Decline" icon={<IconX size={12} />} color={RED} active={d === 'declined'} onClick={() => setDecision(sid, 'declined')} />
                          </Group>
                        </Box>
                      );
                    })}
                  </Stack>
                </ScrollArea.Autosize>
              </Box>
              <TextInput
                label="Authorizer name"
                required
                placeholder="Customer's full name"
                value={authorizerName}
                onChange={(e) => setAuthorizerName(e.currentTarget.value)}
                styles={{
                  input: { background: BG, borderColor: BORDER, color: TEXT },
                  label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 },
                }}
              />
              <Box>
                <Group justify="space-between" mb={6}>
                  <Text size="xs" c={TEXT_MUTED}>Signed paper photo</Text>
                  <SignatureBadge
                    running={signatureCheckRunning}
                    result={signatureResult}
                    hasFile={!!file}
                  />
                </Group>
                <Group gap={10} align="center" mb={previewUrl ? 8 : 0}>
                  <FileButton onChange={handleFileChange} accept="image/*">
                    {(props) => (
                      <Button {...props} size="xs" leftSection={<IconUpload size={14} />} color="haist" variant="outline" loading={uploading}>
                        {file ? 'Replace photo' : 'Choose photo'}
                      </Button>
                    )}
                  </FileButton>
                  {file && <Text size="xs" c={TEXT_SECONDARY} truncate style={{ flex: 1, minWidth: 0 }}>{file.name}</Text>}
                </Group>
                {previewUrl && (
                  <>
                    <SignaturePreview
                      previewUrl={previewUrl}
                      result={signatureResult}
                      binarize={binarize}
                      threshold={binarizeThreshold}
                      cropBox={cropBox}
                      onCropBoxChange={setCropBox}
                      showOverview={showRecrop}
                    />
                    {cropBox && (
                      <Group justify="flex-end" mt={6}>
                        <Button
                          size="compact-xs"
                          variant="outline"
                          color="haist"
                          leftSection={<IconCrop size={12} />}
                          onClick={() => setShowRecrop((v) => !v)}
                        >
                          {showRecrop ? 'Hide re-crop' : 'Re-crop'}
                        </Button>
                      </Group>
                    )}
                    <Box mt={10}>
                      <Group justify="space-between" mb={4}>
                        <Text size="xs" c={TEXT_MUTED}>
                          Cleanup: {binarize ? `Binarize (threshold ${binarizeThreshold})` : 'Off — store original photo'}
                        </Text>
                        <SegmentedControl
                          size="xs"
                          value={binarize ? 'on' : 'off'}
                          onChange={(v) => setBinarize(v === 'on')}
                          data={[{ label: 'Off', value: 'off' }, { label: 'Binarize', value: 'on' }]}
                          styles={{
                            root: { background: BG, border: `1px solid ${BORDER}` },
                            label: { color: TEXT },
                          }}
                        />
                      </Group>
                      {binarize && (
                        <Slider
                          min={64}
                          max={220}
                          step={2}
                          value={binarizeThreshold}
                          onChange={setBinarizeThreshold}
                          label={(v) => `threshold ${v}`}
                          color="haist"
                          styles={{ markLabel: { color: TEXT_MUTED } }}
                        />
                      )}
                    </Box>
                  </>
                )}
              </Box>

              {/* Receipt-link block — inlined to match the DigitalSignatureModal
                  flow. Only shown when we have phone or email to send to. */}
              {customerHasContact && (
                <Box>
                  <Text size="xs" c={TEXT_MUTED} tt="uppercase" fw={600} mb={6}>
                    Send customer a receipt link
                  </Text>
                  <SegmentedControl
                    fullWidth
                    size="xs"
                    value={sendChannel}
                    onChange={(v) => setSendChannel(v as SendChannel)}
                    data={[
                      { label: 'Skip', value: 'skip' },
                      { label: '📲 SMS', value: 'sms' },
                      { label: '✉️ Email', value: 'email' },
                    ]}
                    styles={{
                      root: { background: BG, border: `1px solid ${BORDER}` },
                      indicator: { background: BLUE },
                      label: { color: TEXT },
                    }}
                  />
                  {sendChannel === 'sms' && (
                    <TextInput
                      mt={8}
                      label="Phone"
                      value={phone}
                      onChange={(e) => setPhone(e.currentTarget.value)}
                      placeholder="+1 555 123 4567"
                      styles={{
                        input: { background: BG, borderColor: BORDER, color: TEXT },
                        label: { color: TEXT_SECONDARY, fontSize: 12 },
                      }}
                    />
                  )}
                  {sendChannel === 'email' && (
                    <TextInput
                      mt={8}
                      label="Email"
                      value={email}
                      onChange={(e) => setEmail(e.currentTarget.value)}
                      placeholder="customer@example.com"
                      styles={{
                        input: { background: BG, borderColor: BORDER, color: TEXT },
                        label: { color: TEXT_SECONDARY, fontSize: 12 },
                      }}
                    />
                  )}
                </Box>
              )}

              <Group justify="flex-end">
                <Button size="sm" variant="subtle" color="gray" onClick={handleClose} disabled={saving}>Cancel</Button>
                <Button
                  size="sm"
                  color="haist"
                  onClick={handleSave}
                  loading={saving}
                  disabled={
                    !file
                    || !authorizerName.trim()
                    || signatureCheckRunning
                    || !signatureResult?.signature_detected
                  }
                  title={
                    signatureCheckRunning
                      ? 'Checking signature…'
                      : !signatureResult?.signature_detected
                      ? 'No signature detected — retake or pick another photo'
                      : undefined
                  }
                >
                  {sendChannel === 'skip' || !customerHasContact
                    ? 'Save authorization'
                    : `Save & send ${sendChannel === 'sms' ? 'SMS' : 'email'}`}
                </Button>
              </Group>
        </Stack>
      </Box>
    </Modal>
  );
}

function SignatureBadge({
  running,
  result,
  hasFile,
}: {
  running: boolean;
  result: SignatureResult | null;
  hasFile: boolean;
}) {
  if (!hasFile) return null;
  if (running || !result) {
    return <Badge size="sm" variant="light" color="gray">Checking…</Badge>;
  }
  if (result.signature_detected) {
    return (
      <Badge size="sm" variant="filled" color="green" leftSection={<IconCheck size={12} />}>
        Signature detected
      </Badge>
    );
  }
  return (
    <Badge size="sm" variant="filled" color="red" leftSection={<IconX size={12} />}>
      No signature
    </Badge>
  );
}

function SignaturePreview({
  previewUrl,
  result,
  binarize,
  threshold,
  cropBox,
  onCropBoxChange,
  showOverview,
}: {
  previewUrl: string;
  result: SignatureResult | null;
  binarize: boolean;
  threshold: number;
  cropBox: [number, number, number, number] | null;
  onCropBoxChange: (next: [number, number, number, number]) => void;
  showOverview: boolean;
}) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const [croppedUrl, setCroppedUrl] = useState<string | null>(null);
  const [overviewUrl, setOverviewUrl] = useState<string | null>(null);
  const imgElRef = useRef<HTMLImageElement | null>(null);
  const overviewSvgRef = useRef<SVGSVGElement | null>(null);

  // Load source image once per previewUrl change. Reused by both the
  // cropped preview canvas and the overview thumbnail downscale.
  useEffect(() => {
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (cancelled) return;
      imgElRef.current = img;
      setDims({ w: img.naturalWidth, h: img.naturalHeight });

      // Downscale full image to a thumbnail for the overview panel (max
      // 600px long edge) so the SVG stays light when dragging the box.
      const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
      const scale = longEdge > 600 ? 600 / longEdge : 1;
      const tw = Math.round(img.naturalWidth * scale);
      const th = Math.round(img.naturalHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = tw;
      canvas.height = th;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, tw, th);
      setOverviewUrl(canvas.toDataURL('image/jpeg', 0.75));
    };
    img.src = previewUrl;
    return () => { cancelled = true; img.onload = null; };
  }, [previewUrl]);

  // Re-render the cropped+binarized preview whenever the crop box,
  // binarize toggle, or threshold changes. Capped at 800px on the long
  // edge so dragging stays smooth even at full source resolution.
  useEffect(() => {
    const img = imgElRef.current;
    if (!img || !cropBox) {
      setCroppedUrl(null);
      return;
    }
    const [x1, y1, x2, y2] = cropBox;
    const cw = Math.max(1, x2 - x1);
    const ch = Math.max(1, y2 - y1);
    const longEdge = Math.max(cw, ch);
    const scale = longEdge > 800 ? 800 / longEdge : 1;
    const ow = Math.round(cw * scale);
    const oh = Math.round(ch * scale);
    const canvas = document.createElement('canvas');
    canvas.width = ow;
    canvas.height = oh;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(img, x1, y1, cw, ch, 0, 0, ow, oh);
    if (binarize) {
      const data = ctx.getImageData(0, 0, ow, oh);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        const gray = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
        const v = gray < threshold ? 0 : 255;
        px[i] = v; px[i + 1] = v; px[i + 2] = v;
      }
      ctx.putImageData(data, 0, 0);
    }
    setCroppedUrl(canvas.toDataURL('image/jpeg', 0.85));
  }, [cropBox, binarize, threshold]);

  // Drag-to-move on the overview thumbnail. Pointer events use the SVG's
  // own coord system (viewBox is the source image in pixels), so we
  // translate clientX/Y → SVG coords via getCTM().inverse(). The drag
  // preserves the box size — it only moves x/y.
  const dragRef = useRef<{ startCx: number; startCy: number; startBox: [number, number, number, number] } | null>(null);
  const handlePointerDown = (e: React.PointerEvent<SVGRectElement>) => {
    if (!cropBox || !overviewSvgRef.current) return;
    const svg = overviewSvgRef.current;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    dragRef.current = { startCx: local.x, startCy: local.y, startBox: [...cropBox] as [number, number, number, number] };
    (e.target as Element).setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<SVGRectElement>) => {
    const drag = dragRef.current;
    if (!drag || !overviewSvgRef.current || !dims) return;
    const svg = overviewSvgRef.current;
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const local = pt.matrixTransform(ctm.inverse());
    const dx = local.x - drag.startCx;
    const dy = local.y - drag.startCy;
    const [sx1, sy1, sx2, sy2] = drag.startBox;
    const bw = sx2 - sx1;
    const bh = sy2 - sy1;
    const nx1 = Math.max(0, Math.min(dims.w - bw, sx1 + dx));
    const ny1 = Math.max(0, Math.min(dims.h - bh, sy1 + dy));
    onCropBoxChange([Math.round(nx1), Math.round(ny1), Math.round(nx1 + bw), Math.round(ny1 + bh)]);
  };
  const handlePointerUp = (e: React.PointerEvent<SVGRectElement>) => {
    dragRef.current = null;
    (e.target as Element).releasePointerCapture(e.pointerId);
  };

  const w = dims?.w || result?.page_w || 0;
  const h = dims?.h || result?.page_h || 0;
  if (!w || !h) {
    return (
      <Box style={{ width: '100%', height: 320, background: '#000', borderRadius: 8, border: `1px solid ${BORDER}` }} />
    );
  }

  return (
    <Stack gap={8}>
      {/* Main cropped + binarized preview. While DocAI hasn't returned a
          crop yet (no signature_box → no cropBox → no croppedUrl), show
          a loader instead of the full raw image — the raw photo at this
          scale is huge and confusing right after upload. */}
      <Box
        style={{
          width: '100%',
          minHeight: 180,
          maxHeight: 360,
          background: '#000',
          border: `1px solid ${BORDER}`,
          borderRadius: 8,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {croppedUrl ? (
          <img
            src={croppedUrl}
            alt="signature region"
            style={{ maxWidth: '100%', maxHeight: 360, display: 'block' }}
          />
        ) : (
          <Stack gap={6} align="center">
            <Loader size="sm" color="haist" />
            <Text size="xs" c={TEXT_MUTED}>Detecting signature…</Text>
          </Stack>
        )}
      </Box>

      {/* Overview thumbnail with draggable crop box. Hidden by default;
          opens when the writer clicks the "Re-crop" button (showOverview). */}
      {showOverview && cropBox && overviewUrl && (
        <Box>
          <Text size="xs" c={TEXT_MUTED} mb={4}>
            Drag the box to adjust the crop region
          </Text>
          <Box
            style={{
              width: '100%',
              maxHeight: 220,
              background: '#000',
              border: `1px solid ${BORDER}`,
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <svg
              ref={overviewSvgRef}
              viewBox={`0 0 ${w} ${h}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ width: '100%', height: 'auto', maxHeight: 220, display: 'block', touchAction: 'none' }}
            >
              <image href={overviewUrl} x={0} y={0} width={w} height={h} preserveAspectRatio="none" />
              <rect
                x={cropBox[0]}
                y={cropBox[1]}
                width={cropBox[2] - cropBox[0]}
                height={cropBox[3] - cropBox[1]}
                fill="rgba(34, 197, 94, 0.15)"
                stroke={GREEN}
                strokeWidth={Math.max(6, w / 300)}
                rx={8}
                style={{ cursor: 'move' }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              />
            </svg>
          </Box>
        </Box>
      )}
    </Stack>
  );
}

async function renderCropAndBinarize(
  file: File,
  cropBox: [number, number, number, number],
  threshold: number | null,
): Promise<File> {
  // Single-pass crop + optional binarize for the saved evidence. cropBox
  // is in source-image pixel coords (the same coord system DocAI returned
  // signature_box in). Matches the preview pipeline so what the writer
  // sees in the modal is exactly what lands in GCS.
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const im = new window.Image();
      im.onload = () => resolve(im);
      im.onerror = reject;
      im.src = url;
    });
    const [x1, y1, x2, y2] = cropBox;
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    const cx = Math.max(0, Math.min(W, x1));
    const cy = Math.max(0, Math.min(H, y1));
    const cw = Math.max(1, Math.min(W, x2) - cx);
    const ch = Math.max(1, Math.min(H, y2) - cy);
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d ctx unavailable');
    ctx.drawImage(img, cx, cy, cw, ch, 0, 0, cw, ch);
    if (threshold !== null) {
      const data = ctx.getImageData(0, 0, cw, ch);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        const gray = 0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2];
        const v = gray < threshold ? 0 : 255;
        px[i] = v; px[i + 1] = v; px[i + 2] = v;
      }
      ctx.putImageData(data, 0, 0);
    }
    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/jpeg', 0.9);
    });
    const suffix = threshold !== null ? '.crop.binarized.jpg' : '.crop.jpg';
    const ext = file.name.replace(/\.[^.]+$/, '') + suffix;
    return new File([blob], ext, { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function DecisionBtn({
  label, icon, color, active, onClick,
}: {
  label: string;
  icon?: React.ReactNode;
  color: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      size="compact-xs"
      variant={active ? 'filled' : 'outline'}
      onClick={onClick}
      leftSection={icon}
      styles={{
        root: {
          background: active ? color : 'transparent',
          borderColor: color,
          color: active ? '#fff' : color,
          minWidth: 64,
          height: 26,
        },
      }}
    >
      {label}
    </Button>
  );
}
